/**
 * One registered lookup type, as a card with a table of its values (f2.2).
 *
 * The component is written against the TYPE the server sent, never against a
 * particular one: it reads the label, the description, the reserved codes,
 * whether the type is governed by code and whether it is CLOSED out of the
 * payload. A phase that adds a lookup type gets this screen for free, which
 * is the point of the registry (BR-F2.2-2).
 *
 * `code` is rendered, never edited: application logic resolves values by code,
 * and the runtime database role holds no privilege to update the column. The
 * cell says so on hover rather than leaving a disabled-looking field
 * unexplained.
 *
 * A **closed** type renders with no Add button and no row actions at all --
 * its value set is fixed by application code. That is presentation of a rule,
 * not the rule: the server refuses both writes with `LOOKUP_TYPE_CLOSED`
 * whatever this file does, exactly as it refuses deactivating a reserved
 * value.
 *
 * Rows READ here and are edited in a dialog, which is the pattern the two
 * fixed tables on this screen already use. The table previously held a text
 * input in every cell plus a checkbox beside the status badge; a row that is
 * permanently in edit mode gives the reader no resting state to scan, and the
 * checkbox duplicated the badge sitting next to it.
 */

import { useMemo, useState } from 'react';
import { Info, Lock, Pencil, Plus } from 'lucide-react';

import { ApiError, type LookupType, type LookupValue, type LookupValueUpdate } from '@/api/client';
import { useAddLookupValue, useUpdateLookupValue } from '@/api/queries';
import { SortableHeader } from '@/ui/SortableHeader';
import { nextSort, sortRows, type SortState } from '@/ui/tableSort';

import { DialogField, Modal } from './Modal';
import { isReserved, problemsByKey } from './referenceForm';
import {
  LOOKUP_DEFAULT_SORT,
  lookupSortValue,
  type LookupSortColumn,
} from './referenceSort';

const CODE_IMMUTABLE =
  'A code is what application logic resolves. It cannot be changed once the value exists.';

const RESERVED_HINT =
  'Application logic resolves this value by its code, so it cannot be deactivated. Its name and order can still change.';

type RowDraft = { name: string; sortOrder: string; isActive: boolean };

const draftOf = (value: LookupValue): RowDraft => ({
  name: value.name,
  sortOrder: String(value.sort_order),
  isActive: value.is_active,
});

/** Only the fields that actually differ, so the audit diff names real changes. */
function changesIn(value: LookupValue, draft: RowDraft): LookupValueUpdate {
  const change: LookupValueUpdate = {};
  if (draft.name.trim() !== value.name) change.name = draft.name.trim();
  const order = Number(draft.sortOrder);
  if (draft.sortOrder.trim() !== '' && Number.isFinite(order) && order !== value.sort_order) {
    change.sort_order = order;
  }
  if (draft.isActive !== value.is_active) change.is_active = draft.isActive;
  return change;
}

export function LookupTypePanel({ lookupType }: { lookupType: LookupType }) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<LookupValue | null>(null);
  const [sort, setSort] = useState<SortState<LookupSortColumn>>(LOOKUP_DEFAULT_SORT);

  const ordered = useMemo(
    () => sortRows(lookupType.values, sort, lookupSortValue),
    [lookupType.values, sort],
  );
  const toggleSort = (column: LookupSortColumn) =>
    setSort((current) => nextSort(current, column));

  return (
    <section className="card ref-card ref-panel">
      <div className="card-head">
        <div>
          <h3 className="ref-card-title">{lookupType.label}</h3>
          <span className="hint">{lookupType.description}</span>
        </div>
        <div className="ref-head-actions">
          {lookupType.closed ? (
            <span className="ref-notice" role="note">
              <Lock aria-hidden="true" />
              This set is fixed by the application. Its values cannot be added to or changed.
            </span>
          ) : (
            <>
              {lookupType.governed_by_code && (
                <span className="ref-notice" role="note">
                  <Info aria-hidden="true" />
                  A new value here does nothing until application code names it.
                </span>
              )}
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => setAdding(true)}
              >
                <Plus aria-hidden="true" /> Add value
              </button>
            </>
          )}
        </div>
      </div>
      <div className="card-body">
        {lookupType.values.length === 0 ? (
          <div className="empty">
            <h3>No values yet</h3>
            <p>This type has no values. Add the first one to make it selectable.</p>
          </div>
        ) : (
          <div className="table-scroll">
            <table className="data ref-table">
              <thead>
                {/* Every column sorts except the last, which holds the row
                    actions and has nothing to order by. */}
                <tr>
                  <SortableHeader column="code" label="Code" sort={sort} onSort={toggleSort} />
                  <SortableHeader column="name" label="Name" sort={sort} onSort={toggleSort} />
                  <SortableHeader
                    column="order"
                    label="Order"
                    className="ref-col-order"
                    sort={sort}
                    onSort={toggleSort}
                  />
                  <SortableHeader column="active" label="Status" sort={sort} onSort={toggleSort} />
                  {!lookupType.closed && (
                    <th scope="col">
                      <span className="sr-only">Actions</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {ordered.map((value) => (
                  <LookupRow
                    key={value.id}
                    lookupType={lookupType}
                    value={value}
                    onEdit={() => setEditing(value)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {adding && <AddValueDialog lookupType={lookupType} onClose={() => setAdding(false)} />}
      {editing && (
        <EditValueDialog
          lookupType={lookupType}
          value={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}

function LookupRow({
  lookupType,
  value,
  onEdit,
}: {
  lookupType: LookupType;
  value: LookupValue;
  onEdit: () => void;
}) {
  return (
    <tr>
      <td>
        <span className="mono ref-code" title={CODE_IMMUTABLE}>
          <Lock aria-hidden="true" />
          {value.code}
        </span>
      </td>
      <td>{value.name}</td>
      <td className="ref-col-order">{value.sort_order}</td>
      <td>
        <span className={`badge ${value.is_active ? 'pill-success' : 'badge-muted'}`}>
          {value.is_active ? 'Active' : 'Inactive'}
        </span>
      </td>
      {!lookupType.closed && (
        <td>
          <div className="row-actions">
            <button
              type="button"
              className="btn btn-secondary btn-icon"
              title="Edit value"
              aria-label={`Edit ${value.code}`}
              onClick={onEdit}
            >
              {/* A pencil is honest here, unlike on the Users screen: this
                  action really does edit the record's own fields. */}
              <Pencil aria-hidden="true" />
            </button>
          </div>
        </td>
      )}
    </tr>
  );
}

function EditValueDialog({
  lookupType,
  value,
  onClose,
}: {
  lookupType: LookupType;
  value: LookupValue;
  onClose: () => void;
}) {
  const update = useUpdateLookupValue();
  const [draft, setDraft] = useState<RowDraft>(() => draftOf(value));
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);

  const reserved = isReserved(lookupType, value.code);
  const change = changesIn(value, draft);
  const dirty = Object.keys(change).length > 0;

  const submit = () => {
    if (update.isPending) return;
    // Nothing changed: close rather than send an empty diff the audit trail
    // would record as a change that never happened.
    if (!dirty) {
      onClose();
      return;
    }
    setProblems({});
    setMessage(null);
    update.mutate(
      { typeCode: lookupType.code, valueId: value.id, change },
      {
        onSuccess: onClose,
        onError: (error) => {
          // The dialog STAYS OPEN and keeps what was typed.
          if (error instanceof ApiError && error.status < 500) {
            setProblems(problemsByKey(error.errors));
            setMessage(error.errors.some((item) => item.key) ? null : error.message);
            return;
          }
          setMessage(error instanceof Error ? error.message : 'The value could not be saved.');
        },
      },
    );
  };

  return (
    <Modal
      title={`Edit ${value.name}`}
      subtitle={`Code ${value.code} cannot be changed.`}
      onClose={onClose}
      onSubmit={submit}
      busy={update.isPending}
      submitLabel={update.isPending ? 'Saving…' : 'Save value'}
      message={message}
    >
      <DialogField label="Name" htmlFor="edit-value-name" problem={problems.name}>
        <input
          id="edit-value-name"
          type="text"
          value={draft.name}
          onChange={(event) => setDraft({ ...draft, name: event.target.value })}
          disabled={update.isPending}
        />
      </DialogField>
      <DialogField label="Order" htmlFor="edit-value-order" problem={problems.sort_order}>
        <input
          id="edit-value-order"
          type="number"
          value={draft.sortOrder}
          onChange={(event) => setDraft({ ...draft, sortOrder: event.target.value })}
          disabled={update.isPending}
        />
      </DialogField>
      <DialogField
        label="Status"
        htmlFor="edit-value-active"
        problem={problems.is_active}
        hint={reserved ? RESERVED_HINT : undefined}
        span
      >
        <label className="check">
          <input
            id="edit-value-active"
            type="checkbox"
            checked={draft.isActive}
            // A reserved value cannot be deactivated: the SERVER refuses it.
            // Disabling the control explains that before the refusal, and is
            // not the control itself -- the 409 is.
            disabled={reserved || update.isPending}
            onChange={(event) => setDraft({ ...draft, isActive: event.target.checked })}
          />
          Selectable for new records
        </label>
      </DialogField>
    </Modal>
  );
}

function AddValueDialog({
  lookupType,
  onClose,
}: {
  lookupType: LookupType;
  onClose: () => void;
}) {
  const add = useAddLookupValue();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);

  const submit = () => {
    if (add.isPending) return;
    setProblems({});
    setMessage(null);
    const order = Number(sortOrder);
    add.mutate(
      {
        typeCode: lookupType.code,
        value: {
          code,
          name,
          // Omitted rather than guessed: the server places it last.
          sort_order: sortOrder.trim() === '' || !Number.isFinite(order) ? null : order,
        },
      },
      {
        onSuccess: onClose,
        onError: (error) => {
          // The dialog STAYS OPEN and keeps what was typed: a refusal the
          // user has to retype is a refusal they will get wrong twice.
          if (error instanceof ApiError && error.status < 500) {
            setProblems(problemsByKey(error.errors));
            setMessage(error.errors.some((item) => item.key) ? null : error.message);
            return;
          }
          setMessage(error instanceof Error ? error.message : 'The value could not be added.');
        },
      },
    );
  };

  return (
    <Modal
      title={`Add a ${lookupType.label.toLocaleLowerCase()} value`}
      subtitle={lookupType.description}
      onClose={onClose}
      onSubmit={submit}
      busy={add.isPending}
      submitLabel={add.isPending ? 'Adding…' : 'Add value'}
      message={message}
    >
      <DialogField
        label="Code"
        htmlFor="lookup-code"
        problem={problems.code}
        hint="Upper-case letters, digits and underscores, starting with a letter. It cannot be changed later."
      >
        <input
          id="lookup-code"
          type="text"
          className="mono"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          disabled={add.isPending}
        />
      </DialogField>
      <DialogField label="Name" htmlFor="lookup-name" problem={problems.name}>
        <input
          id="lookup-name"
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          disabled={add.isPending}
        />
      </DialogField>
      <DialogField
        label="Order"
        htmlFor="lookup-order"
        problem={problems.sort_order}
        hint="Leave empty to place it last."
        span
      >
        <input
          id="lookup-order"
          type="number"
          value={sortOrder}
          onChange={(event) => setSortOrder(event.target.value)}
          disabled={add.isPending}
        />
      </DialogField>
    </Modal>
  );
}
